import {
  IAdminPharmacyListBaseData,
  ServiceTypes,
  TypeOfUser,
  convertToDecimals,
  fallbackText,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import {
  Constants,
  DynamicTableCell,
  OtherScreens,
} from '../../../helpers/Constants';

import strings from '../../../localization';
import {
  keysToRemove as AppointedKeysToRemove,
  IPharmacyListActionsParams,
} from '../../../hooks/Admin/PharmacyList/usePharmacyListAction';
import { IMenuActions } from '../../common/CustomAvatarSection/CustomAvatarSection';
import { getDayMonthDateYear } from '../../Reports/helpers';
import { IUserProfileLinkSignature } from '../../../hooks/Admin/useUserProfileLink';
import { IDynamicTableHeader } from '../../DynamicTable/types';

const { text, customLink, customMenu, button, icon, checkbox } = DynamicTableCell;
const { text: textVariant } = Constants.variant;
const { Deactivate, ViewPharmacyAdmins } = AppointedKeysToRemove;
const keysToRemove = [Deactivate, ViewPharmacyAdmins];

const planTypeHeader = 'planTypeHeader';
const ownerHeader = 'ownerHeader';
const selectionHeader = 'selectionHeader';
const isFullService = ServiceTypes.full === ServiceHandler.getService();

const ownerCell = (pharmacyId: string, ownerName?: null | string) =>
  ({
    key: `${ownerHeader}-${pharmacyId}`,
    value: ownerName || strings.unassigned,
    type: text,
    maxWidth: 200,
  });

export const activePharmaciesHeader = (showSelection = false) => {
  let headers = [
    {
      key: '0',
      label: strings.banner,
    },
    {
      key: '1',
      label: strings.bannerNumber,
      sortingKey: 'bannerNumber',
    },
    {
      key: '2',
      label: strings.pharmacyName,
      sortingKey: 'name',
    },
    {
      key: '3',
      label: strings.contactName,
      sortingKey: 'contactName',
    },
    {
      key: '4',
      label: strings.email,
      sortingKey: 'email',
    },
    {
      key: '5',
      label: strings.mobile,
      sortingKey: 'mobile',
    },
    {
      key: '6',
      label: strings.city,
      sortingKey: 'city',
    },
    isFullService && {
      key: '7',
      label: strings.pharmacyHourlyRate,
      sortingKey: 'pharmacyHourlyRate',
    },
    {
      key: planTypeHeader,
      label: strings.planType,
      sortingKey: 'planName',
    },
    {
      key: ownerHeader,
      label: strings.owner,
      sortingKey: 'ownerAdminName',
    },
    {
      key: '8',
      label: strings.action,
      isCenterAligned: true,
    },
    showSelection && {
      key: selectionHeader,
      label: strings.select,
      isCenterAligned: true,
    },
  ].filter(Boolean) as Array<IDynamicTableHeader>;

  if (isFullService) {
    headers = headers.filter((item) =>
      item.key !== planTypeHeader);
  }

  return headers;
};

export const deactivatedPharmaciesHeader = () =>
  [
    ...activePharmaciesHeader().slice(0, 6),
    { key: '6', label: strings.deactivatedDate, sortingKey: 'lastActivityDate' },
    ...activePharmaciesHeader().slice(6),
  ];

export const deletedPharmaciesHeader = () => {
  let headers = [
    {
      key: '0',
      label: strings.banner,
    },
    {
      key: '1',
      label: strings.bannerNumber,
      sortingKey: 'bannerNumber',
    },
    {
      key: '2',
      label: strings.pharmacyName,
      sortingKey: 'name',
    },
    {
      key: '3',
      label: strings.contactName,
      sortingKey: 'contactName',
    },
    {
      key: '4',
      label: strings.deletedDate,
      sortingKey: 'deletedDate',
    },
    isFullService && {
      key: '5',
      label: strings.pharmacyHourlyRate,
      isCenterAligned: true,
      sortingKey: 'pharmacyHourlyRate',
    },
    {
      key: '6',
      label: strings.planType,
      sortingKey: 'planName',
    },
  ].filter(Boolean) as Array<IDynamicTableHeader>;

  if (isFullService) {
    headers = headers.filter((item) =>
      item.key !== planTypeHeader);
  }

  return headers;
};

export interface IPharmacyRowSelection {
  selectedIds: Array<string>;
  handlePress: (id: string) => () => void;
}

export const activePharmaciesList = (
  data: IAdminPharmacyListBaseData[],
  getImage: (logoId: string) => string,
  goToUserProfile: IUserProfileLinkSignature,
  actions?: (params: IPharmacyListActionsParams) => Array<IMenuActions>,
  selection?: IPharmacyRowSelection | null,
) =>
  data.map((item) => {
    const {
      logoId,
      name,
      bannerNumber,
      pharmacyId,
      contactName,
      email,
      mobile,
      pharmacyHourlyRate,
      city,
      appointedPharmacyId,
      planName,
      userId,
      ownerAdminId,
      ownerAdminName,
    } = item ?? {};

    const fromScreen = appointedPharmacyId
      ? OtherScreens.AppointedPharmacies
      : null;

    const planData = isFullService
      ? false
      : {
        key: planTypeHeader,
        value: planName ?? fallbackText,
        type: text,
      };

    return {
      key: pharmacyId,
      data: [
        {
          key: `0-${pharmacyId}`,
          value: getImage(logoId),
          type: icon,
        },
        {
          key: `1-${pharmacyId}`,
          value: bannerNumber,
          type: text,
        },
        {
          key: `2-${pharmacyId}`,
          maxWidth: 200,
          value: {
            label: name,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
              fromScreen,
            }),
          },
          type: customLink,
        },
        {
          key: `3-${pharmacyId}`,
          value: contactName,
          maxWidth: 200,
          type: text,
        },
        {
          key: `4-${pharmacyId}`,
          value: email,
          type: text,
          maxWidth: 200,
        },
        {
          key: `5-${pharmacyId}`,
          value: mobile,
          type: text,
        },
        {
          key: `6-${pharmacyId}`,
          value: city,
          type: text,
        },
        isFullService && {
          key: `7-${pharmacyId}`,
          value: `$${convertToDecimals(pharmacyHourlyRate)}`,
          isCenterAligned: true,
          type: text,
        },
        planData,
        ownerCell(pharmacyId, ownerAdminName),
        {
          key: `8-${pharmacyId}`,
          value: {
            customMenuActions: actions?.({
              pharmacyId,
              name,
              email,
              appointedPharmacyId,
              planName,
              userId,
              ownerId: ownerAdminId,
            }),
          },
          type: customMenu,
        },
        selection && {
          key: `${selectionHeader}-${pharmacyId}`,
          value: {
            checked: selection.selectedIds.includes(pharmacyId),
            handlePress: selection.handlePress(pharmacyId),
          },
          type: checkbox,
        },
      ].filter(Boolean),
    };
  });

export const deactivatedPharmaciesList = (
  data: IAdminPharmacyListBaseData[],
  getImage: (logoId: string) => string,
  goToUserProfile: IUserProfileLinkSignature,
  activateModal: (pharmacyId: string) => () => void,
) =>
  data.map((item) => {
    const {
      logoId,
      name,
      bannerNumber,
      pharmacyId,
      contactName,
      email,
      mobile,
      lastActivityDate,
      pharmacyHourlyRate,
      city,
      planName,
      ownerAdminName,
    } = item ?? {};

    const deactivatedDate = getDayMonthDateYear(lastActivityDate ?? '');
    const planData = isFullService
      ? {}
      : {
        key: planTypeHeader,
        value: planName ?? fallbackText,
        type: text,
      };

    return {
      key: pharmacyId,
      data: [
        {
          key: `0-${pharmacyId}`,
          value: getImage(logoId),
          type: icon,
        },
        {
          key: `1-${pharmacyId}`,
          value: bannerNumber,
          type: text,
        },
        {
          key: `2-${pharmacyId}`,
          maxWidth: 200,
          value: {
            label: name,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
            }),
          },
          type: customLink,
        },
        {
          key: `3-${pharmacyId}`,
          value: contactName,
          maxWidth: 200,
          type: text,
        },
        {
          key: `-4-${pharmacyId}`,
          value: email,
          type: text,
          maxWidth: 200,
        },
        {
          key: `4-${pharmacyId}`,
          value: mobile,
          type: text,
        },
        {
          key: `5-${pharmacyId}`,
          value: deactivatedDate,
          type: text,
        },
        {
          key: `6-${pharmacyId}`,
          value: city,
          type: text,
        },
        isFullService && {
          key: `7-${pharmacyId}`,
          value: `$${convertToDecimals(pharmacyHourlyRate)}`,
          isCenterAligned: true,
          type: text,
        },
        planData,
        ownerCell(pharmacyId, ownerAdminName),
        {
          key: `8-${pharmacyId}`,
          value: {
            label: strings.activate,
            handlePress: activateModal(pharmacyId),
            variant: textVariant,
          },
          show: true,
          type: button,
        },
      ].filter(Boolean),
    };
  });

export const deletedPharmaciesList = (
  data: IAdminPharmacyListBaseData[],
  getImage: (logoId: string) => string,
  goToUserProfile: IUserProfileLinkSignature,
) =>
  data.map((item) => {
    const {
      logoId,
      name,
      bannerNumber,
      pharmacyId,
      contactName,
      deletedDate,
      userId,
      pharmacyHourlyRate,
      planName,
    } = item ?? {};

    const date = getDayMonthDateYear(deletedDate ?? '');
    const planData = isFullService
      ? {}
      : {
        key: planTypeHeader,
        value: planName ?? fallbackText,
        type: text,
      };

    return {
      key: pharmacyId,
      data: [
        {
          key: `0-${pharmacyId}`,
          value: getImage(logoId),
          type: icon,
        },
        {
          key: `1-${pharmacyId}`,
          value: bannerNumber,
          type: text,
        },
        {
          key: `2-${pharmacyId}`,
          maxWidth: 200,
          value: {
            label: name,
            handlePress: goToUserProfile({
              personnelId: userId,
              userType: TypeOfUser.pharmacy,
              fromScreen: OtherScreens.DeletedPharmacyList,
            }),
          },
          type: customLink,
        },
        {
          key: `3-${pharmacyId}`,
          value: contactName,
          maxWidth: 200,
          type: text,
        },
        {
          key: `4-${pharmacyId}`,
          value: date,
          type: text,
        },
        isFullService && {
          key: `5-${pharmacyId}`,
          value: `$${convertToDecimals(pharmacyHourlyRate)}`,
          isCenterAligned: true,
          type: text,
        },
        planData,
      ].filter(Boolean),
    };
  });

export const appointedPharmaciesList = (
  data: IAdminPharmacyListBaseData[],
  getImage: (logoId: string) => string,
  goToUserProfile: IUserProfileLinkSignature,
  actions?: (params: IPharmacyListActionsParams) => Array<IMenuActions>,
) =>
  data.map((item) => {
    const {
      logoId,
      name,
      bannerNumber,
      pharmacyId,
      contactName,
      email,
      mobile,
      pharmacyHourlyRate,
      city,
      planName,
      userId,
      ownerAdminId,
      ownerAdminName,
    } = item ?? {};
    const planData = isFullService
      ? {}
      : {
        key: planTypeHeader,
        value: planName ?? fallbackText,
        type: text,
      };

    return {
      key: pharmacyId,
      data: [
        {
          key: `0-${pharmacyId}`,
          value: getImage(logoId),
          type: icon,
        },
        {
          key: `1-${pharmacyId}`,
          value: bannerNumber,
          type: text,
        },
        {
          key: `2-${pharmacyId}`,
          maxWidth: 200,
          value: {
            label: name,
            handlePress: goToUserProfile({
              personnelId: pharmacyId,
              fromScreen: OtherScreens.AppointedPharmacies,
              userType: TypeOfUser.pharmacy,
            }),
          },
          type: customLink,
        },
        {
          key: `3-${pharmacyId}`,
          value: contactName,
          maxWidth: 200,
          type: text,
        },
        {
          key: `4-${pharmacyId}`,
          value: email,
          type: text,
          maxWidth: 200,
        },
        {
          key: `5-${pharmacyId}`,
          value: mobile,
          type: text,
        },
        {
          key: `6-${pharmacyId}`,
          value: city,
          type: text,
        },
        isFullService && {
          key: `7-${pharmacyId}`,
          value: `$${convertToDecimals(pharmacyHourlyRate)}`,
          isCenterAligned: true,
          type: text,
        },
        planData,
        ownerCell(pharmacyId, ownerAdminName),
        {
          key: `8-${pharmacyId}`,
          value: {
            customMenuActions: actions?.({
              pharmacyId,
              name,
              email,
              userId,
              ownerId: ownerAdminId,
            }).filter(
              (inItem) =>
                !keysToRemove.includes(inItem.key),
            ),
          },
          type: customMenu,
        },
      ].filter(Boolean),
    };
  });
